/**
 * 真人页来源条(0924 车 D 二期):说清楚「框里这份稿从哪来」。
 *   · 样片模板:名字 + 槽位小输入框(`{行业}{产品}{场景}{口播}`)—— 填了再点「帮我写脚本」,
 *     AI 按模板 + 槽位写完整稿;不填也能直接改框里的字;
 *   · 追爆蓝图:名字 + 「只学套路,原片画面和口播一个字都不带过来」+ 重新按套路写。
 */
import { Flame, LayoutTemplate, X } from "lucide-react";

import type { RealPersonExplosiveHandoff } from "@/entities/explosive";
import { PLATFORM_LABEL } from "@/shared/lib/platformLabel";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import type { VideoTemplateDto } from "../../../api/videoService";
import { templateSlots } from "../../../model/realPersonIntake";

export function RealPersonTemplateStrip({
  template,
  slotValues,
  onSlotChange,
  onClear,
}: {
  template: VideoTemplateDto;
  slotValues: Record<string, string>;
  onSlotChange: (slot: string, value: string) => void;
  onClear: () => void;
}) {
  const slots = templateSlots(template.briefScript);
  return (
    <div
      data-testid="real-template-strip"
      className="space-y-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5"
    >
      <div className="flex items-center gap-2 text-xs">
        <LayoutTemplate className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate">
          正在用样片「<span className="font-medium">{template.name}</span>」
          {slots.length > 0 && ",填下面几项再点「帮我写脚本」,AI 按你的内容写完整"}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-6 px-2"
          aria-label="不用这个样片"
          onClick={onClear}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      {slots.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {slots.map((s) => (
            <Input
              key={s}
              aria-label={`样片槽位 ${s}`}
              data-testid={`real-template-slot-${s}`}
              value={slotValues[s] ?? ""}
              maxLength={200}
              placeholder={s}
              onChange={(e) => onSlotChange(s, e.target.value)}
              className="h-8 text-xs"
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function RealPersonExplosiveStrip({
  handoff,
  writing,
  onRewrite,
}: {
  handoff: RealPersonExplosiveHandoff;
  writing: boolean;
  onRewrite: () => void;
}) {
  const platform = handoff.sourcePlatform
    ? (PLATFORM_LABEL[handoff.sourcePlatform] ?? handoff.sourcePlatform)
    : null;
  return (
    <div
      data-testid="real-explosive-strip"
      className="flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5 text-xs"
    >
      <Flame className="h-3.5 w-3.5 shrink-0 text-primary" />
      <span className="min-w-0 flex-1">
        按爆款蓝图「<span className="font-medium">{handoff.formulaName}</span>」
        {platform ? `(来自${platform})` : ""}的套路写 ——
        只学结构和拍法,原片画面和口播一个字都不带过来。
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={writing}
        data-testid="real-explosive-rewrite"
        onClick={onRewrite}
      >
        按这个套路重新写
      </Button>
    </div>
  );
}
